import React, { useState, useMemo, useEffect } from 'react';
import { useStore } from 'react-redux';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';

import NestedQMenu from 'components/NestedQMenu';
import QButton from 'components/QButton';
import SearchBox from 'components/SearchBox';
import ZeroStateView from 'components/ZeroStateView';

import RuleSummaryRow from './RuleSummaryRow';
import { SourceHeading, TargetHeading } from './reviewConditions/Headings';

import { getFilteredRules } from 'app-components/flux/transaction-rules/transactionRulesSelectors';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { tracker } from 'app-components/utils/core';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  background: {
    background: theme.palette.background.default,
  },
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: '0 !important',
  },
  containerRow: {
    display: 'flex',
    '&:not(:last-of-type)': {
      borderBottom: `1px ${theme.palette.divider} solid`,
    },
    '>*': {
      display: 'flex',
      flex: 1,
      '&:not(:last-of-type)': {
        borderRight: `1px ${theme.palette.divider} solid`,
      },
    },
  },
  row: {
    display: 'flex',
    flex: 1,
    background: theme.palette.background.default,
  },
  // column: {
  //   display: 'flex',
  //   flex: 1,
  //   padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  //   background: theme.palette.background.default,
  // },
  // threeDotMenu: {
  //   '&:hover': {
  //     cursor: 'pointer',
  //   },
  // },
  cardHeader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    gap: 8,
    '& .MuiCardHeader-action': {
      margin: 0,
    },
  },
  ruleListActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  // rulesSortFilter: {
  //   borderRadius: '8px',
  //   height: '32px',
  //   width: '175px',
  //   '& .MuiInputBase-input': {
  //     fontSize: '14px',
  //   },
  // },
  zeroState: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    flex: 0,
    alignItems: 'center',
    gap: theme.spacing(2),
    height: '50vh',
  },
}));

// const ruleSortOptions = [
//   {
//     name: 'By Newest',
//     value: 0,
//   },
//   {
//     name: 'By Most Recent',
//     value: 1,
//   },
//   {
//     name: 'By Theme',
//     value: 2,
//   },
// ];

type RulesListProps = {
  rules: TransactionRuleType[];
  // onOpenEditorCallback: () => void;
  onEditCallback: (id?: string) => void;
  onDeleteCallback?: (id: string) => void;
  onDuplicateCallback?: (id: string) => void;
};

const RulesList: React.FC<RulesListProps> = ({
  rules,
  onEditCallback,
  onDeleteCallback,
  // onDuplicateCallback,
}) => {
  const { cx, classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [filteredRules, setFilteredRules] = useState<TransactionRuleType[]>(rules);
  const [searchValue, setSearchValue] = useState<string>();
  const store = useStore();

  // Not for Phase 1
  const menuOptions = useMemo(
    () => [
      // TBD
    ],
    [],
  );

  useEffect(() => {
    if (searchValue) {
      const filtered = getFilteredRules(store.getState(), searchValue);
      setFilteredRules(filtered);
      tracker.track(tracker.events.txnRuleSearch);
    } else {
      setFilteredRules(rules);
    }
  }, [rules, searchValue, store]);

  return (
    <>
      <NestedQMenu
        id='txn-rules-three-dot-menu'
        key={`three-dot`}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        options={menuOptions}
      />
      <Card id='rules-list-container'>
        <CardHeader
          id='rules-list-count'
          className={classes.cardHeader}
          title={
            filteredRules.length < rules.length
              ? `${filteredRules.length} of ${rules.length} Rules`
              : `${rules.length} Rules`
          }
          titleTypographyProps={{ variant: 'titleMedium' }}
          action={
            <Box className={classes.ruleListActions}>
              <SearchBox
                id='rules-search-box'
                placeholder='Search rules'
                onSearch={setSearchValue}
                initialValue={searchValue}
                autoSearch
                autoFocus
              />
              {/* <Select
                id='rules-sort-select'
                className={classes.rulesSortFilter}
                value={rulesSortOrder}
                onChange={handleSortChange}
              >
                {ruleSortOptions.map(({ name, value }) => (
                  <MenuItem key={name} value={value}>
                    {name}
                  </MenuItem>
                ))}
              </Select> */}
              {/* <IconButton
                id='rules-list-three-dot-menu'
                className={classes.threeDotMenu}
                aria-label='three-dot-menu'
                onClick={(e: any) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
              >
                <ThreeDot />
              </IconButton> */}
            </Box>
          }
        />

        {rules.length === 0 ? (
          <CardContent className={classes.zeroState} id='rules-list-zero-state'>
            <ZeroStateView
              size='small'
              id='rules-list-zero-state'
              title='Rules you create will appear here.'
              qIllustration='rules'
              button={
                <QButton variant='add' id='rule-create-button' onClick={() => onEditCallback()}>
                  Rule
                </QButton>
              }
            />
          </CardContent>
        ) : (
          <CardContent className={classes.container} id='rules-list'>
            <Box className={cx(classes.containerRow, classes.row)}>
              <SourceHeading className={classes.background} />
              <TargetHeading className={classes.background} />
            </Box>
            {filteredRules &&
              filteredRules.map((rule) => (
                <Box className={classes.containerRow} key={rule.id || 0}>
                  <RuleSummaryRow
                    rule={rule}
                    onEditCallback={onEditCallback}
                    onDeleteCallback={onDeleteCallback}
                    // onDuplicateCallback={onDuplicateCallback}
                  />
                </Box>
              ))}
          </CardContent>
        )}
      </Card>
    </>
  );
};

export default RulesList;
